
import { useNavigate } from "react-router-dom";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { Influencer } from "@/types/influencer";
import { Star, Clock, Calendar, Eye } from "lucide-react";
import { FaTiktok, FaInstagram, FaYoutube, FaFacebook, FaXTwitter } from "react-icons/fa6";
import { useState } from "react";
import { motion } from "framer-motion";

interface InfluencerCardProps {
  influencer: Influencer;
  compact?: boolean;
}

const SocialIcon = ({ platform, url, followers }: { platform: string; url?: string; followers?: string }) => {
  if (!url && !followers) return null;
  
  const icons: Record<string, React.ReactNode> = {
    instagram: <FaInstagram className="h-3.5 w-3.5 text-pink-500" />,
    youtube: <FaYoutube className="h-3.5 w-3.5 text-red-500" />,
    tiktok: <FaTiktok className="h-3.5 w-3.5" />,
    facebook: <FaFacebook className="h-3.5 w-3.5 text-blue-600" />,
    twitter: <FaXTwitter className="h-3.5 w-3.5" />,
  };

  return (
    <a 
      href={url} 
      target="_blank" 
      rel="noopener noreferrer"
      className="flex items-center gap-1 text-xs bg-muted/50 hover:bg-muted px-2 py-1 rounded-full transition-colors"
      onClick={(e) => e.stopPropagation()}
    >
      {icons[platform]}
      {followers && <span className="text-muted-foreground">{followers}</span>}
    </a>
  );
};

export const InfluencerCard = ({ influencer, compact = false }: InfluencerCardProps) => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  
  const handleViewDetails = () => {
    navigate(`/influencers/${influencer.id}`);
  };
  
  const handleBookNow = () => {
    navigate(`/influencer-booking/${influencer.id}`);
  };

  const socialLinks = influencer.socialLinks || {};
  const hasSocialLinks = Object.keys(socialLinks).some(
    key => key.endsWith('_followers') && socialLinks[key as keyof typeof socialLinks]
  );
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -5 }}
    >
      <Card 
        className="overflow-hidden transition-all duration-500 h-full flex flex-col group relative border-primary/5 rounded-xl shadow-sm hover:shadow-xl"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-700 -z-10"></div>
        
        <div className="relative h-72 overflow-hidden rounded-t-xl">
          <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent z-10 opacity-60 ${isHovered ? 'opacity-80' : ''} transition-opacity duration-500`}></div>
          
          <img
            src={influencer.image}
            alt={influencer.name}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          
          {influencer.featured && (
            <Badge className="absolute top-3 right-3 z-20 bg-primary/90 backdrop-blur-sm py-1 px-3">
              <motion.span
                animate={{ opacity: [1, 0.8, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                {t("featured")}
              </motion.span>
            </Badge>
          )}
          
          <div className={`absolute bottom-0 left-0 right-0 p-4 z-20 transform ${isHovered ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'} transition-all duration-500`}>
            <div className="flex justify-between items-center">
              <Button 
                size="sm" 
                variant="outline"
                onClick={handleViewDetails}
                className="bg-white/20 backdrop-blur-md border-white/30 text-white hover:bg-white/30 hover:text-white"
              >
                <Eye className="h-4 w-4 mr-1" />
                {t("view")}
              </Button>
              {!compact && (
                <Button
                  size="sm"
                  onClick={handleBookNow}
                  className="bg-primary/90 backdrop-blur-md border-primary/30 hover:bg-primary text-white"
                >
                  <Calendar className="h-4 w-4 mr-1" />
                  {t("book")}
                </Button>
              )}
            </div>
          </div>
        </div>
        
        <div className="p-5 pt-6 flex-grow bg-gradient-to-b from-transparent to-primary/5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center">
              <Avatar className="h-12 w-12 mr-3 border-2 border-primary/20 ring-2 ring-white/80 shadow-md">
                <img src={influencer.image} alt={influencer.name} className="object-cover" />
              </Avatar>
              <div>
                <h3 className="font-semibold text-lg">{influencer.name}</h3>
                <p className="text-sm text-muted-foreground">{influencer.category}</p>
              </div>
            </div>
          </div>

          {/* Social Media Links with Followers */}
          {hasSocialLinks && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              <SocialIcon 
                platform="instagram" 
                url={socialLinks.instagram} 
                followers={socialLinks.instagram_followers} 
              />
              <SocialIcon 
                platform="youtube" 
                url={socialLinks.youtube} 
                followers={socialLinks.youtube_followers} 
              />
              <SocialIcon 
                platform="tiktok" 
                url={socialLinks.tiktok} 
                followers={socialLinks.tiktok_followers} 
              />
              <SocialIcon 
                platform="facebook" 
                url={socialLinks.facebook} 
                followers={socialLinks.facebook_followers} 
              />
              <SocialIcon 
                platform="twitter" 
                url={socialLinks.twitter} 
                followers={socialLinks.twitter_followers} 
              />
            </div>
          )}
          
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center">
              <Star className="h-4 w-4 text-primary fill-primary mr-1" />
              <span className="text-sm font-medium">N/A</span>
            </div>
            <div className="flex items-center text-sm text-muted-foreground">
              <Clock className="h-3 w-3 mr-1" />
              <span>{influencer.availability}</span>
            </div>
          </div>
        </div>
        
        <div className="p-5 pt-0 flex justify-between items-center border-t mt-auto border-primary/10">
          <div className="text-lg font-bold text-primary">N/A SAR</div>
          {compact ? (
            <Button 
              size="sm" 
              variant="outline"
              onClick={handleViewDetails}
              className="rounded-full hover:bg-primary/5 border-primary/10"
            >
              <Eye className="h-4 w-4" />
            </Button>
          ) : (
            <Button 
              size="sm"
              onClick={handleBookNow}
              className="bg-gradient-to-r from-primary to-primary/80 hover:shadow-md transition-shadow rounded-full px-4"
            >
              <Calendar className="h-4 w-4 mr-1" />
              {t("book")}
            </Button>
          )}
        </div>
      </Card>
    </motion.div>
  );
};
